import { Component, inject, OnInit, Renderer2, signal } from '@angular/core';
import { ActivatedRoute, Data, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { ModalCookiesComponent } from './core/components/modal-cookies/modal-cookies.component';
import { FooterComponent } from './core/components/footer/footer.component';
import { filter, map } from 'rxjs';
import { HeaderComponent } from './core/components/header/header.component';
import { MobileButtonsComponent } from './core/components/mobile-buttons/mobile-buttons.component';
import { HelpBoxComponent } from './core/components/help-box/help-box.component';
import { SpinnerComponent } from '@utilities/components/spinner/spinner.component';
import { OktaAuthStateService } from '@okta/okta-angular';
import { AuthState } from '@okta/okta-auth-js';
import { AsyncPipe } from '@angular/common';
import { AuthService } from './auth/services/auth.service';
import { OneSignal, OnesignalNgxModule } from 'onesignal-ngx';
import { environment } from '../environments/environment';
import { TagManagerService } from '@utilities/services/tag-manager.service';
import { Title } from '@angular/platform-browser';
import { TokenService } from '@utilities/services/token.service';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    ModalCookiesComponent,
    FooterComponent,
    HeaderComponent,
    MobileButtonsComponent,
    HelpBoxComponent,
    SpinnerComponent,
    AsyncPipe,    
  ],
  template: `
    <app-mobile-buttons />
    @if((isAuthenticated$ | async) || authService.isLoggedIn()) {
      @if(!tokenService.isLoginSharedWishList()){
    <app-header />
      }
    }
    <router-outlet />
    <app-modal-cookies />
    @if(isVisibleFooter()){
    <app-footer />
    } @if((isAuthenticated$ | async) || authService.isLoggedIn()) {
    <app-help-box />
    }
    <app-spinner spinnerType="global" />
  `,
})
export class AppComponent implements OnInit {
  private router = inject(Router);
  private renderer2 = inject(Renderer2);
  private activatedRoute = inject(ActivatedRoute);
  authService = inject(AuthService);
  tokenService = inject(TokenService);
  isVisibleFooter = signal(true);
  private gtm = inject(TagManagerService);
  private titleSrv = inject(Title);
  private bodyClasses: string[] = this.router.config
    .map((route) => route.data)
    .filter((data) => !!data)
    .map((d: Data) => d['bodyClass'])
    .filter((bc) => !!bc);

  private oktaStateService = inject(OktaAuthStateService);
  public isAuthenticated$ = this.oktaStateService.authState$.pipe(
    filter((s: AuthState) => !!s),
    map((s: AuthState) => s.isAuthenticated ?? false)
  );

  private oneSignal = inject(OneSignal);
  constructor() {
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe((e: any) => {
        this.gtm.pageView(e.urlAfterRedirects);
      });
  }

  ngOnInit() {
    if (this.authService.isLoggedIn()) {
      this.gtm.hydrateUserContextFromApi();
    }

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map((event: NavigationEnd) => event.urlAfterRedirects)
      )
      .subscribe((urlAfterRedirects: string) => {
        const currentUrl = urlAfterRedirects.split('/')[1] ?? '';

        this.bodyClasses.forEach((c) => this.renderer2.removeClass(document.body, c));
        const data = this.router.config.find((r) => r.path === currentUrl)?.data;

        if (data?.['bodyClass']) this.renderer2.addClass(document.body, data['bodyClass']);
        this.setFooterVisibilityByUrl(currentUrl);

        const moduleName =
          this.getDeepestRouteTitle()
          ?? this.titleSrv.getTitle()
          ?? (currentUrl || 'Home');

        this.gtm.setModule(moduleName);
        this.gtm.pageView(urlAfterRedirects, moduleName);
      });
  }

  private getDeepestRouteTitle(): string | null {
    let route = this.activatedRoute;
    while (route.firstChild) route = route.firstChild;

    const tFromData = route.snapshot.data?.['title'];
    if (typeof tFromData === 'string' && tFromData.trim()) return tFromData;

    const tFromSnapshot: any = (route.snapshot as any).title;
    if (typeof tFromSnapshot === 'string' && tFromSnapshot.trim()) return tFromSnapshot;

    return null;
  }

  setFooterVisibilityByUrl(currentUrl: string) {
    const urlsHideFooter = ['login-gpid'];
    this.isVisibleFooter.set(!urlsHideFooter.includes(currentUrl));
  }
}
